import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import {
  GetIntegrationHistoryParams,
  IntegrationsHistoryItem,
} from "features/integrations/integrations";
import { formatDateTime, formatWeekday } from "utils/dateUtils";

interface IntegrationDetailsTableProps {
  history: IntegrationsHistoryItem[];
  isFetching: boolean;
  sortBy: GetIntegrationHistoryParams["sort_by"];
  sortOrder: "asc" | "desc";
  onSortChange: (field: GetIntegrationHistoryParams["sort_by"]) => void;
  timezone?: string;
}

const headerCellStyles = {
  fontWeight: "bold",
  color: "white",
  fontSize: "1rem",
  border: "1px solid black",
  borderColor: "primary.main",
  backgroundColor: "primary.main",
  textAlign: "center",
};

const bodyCellStyles = {
  fontWeight: 300,
  fontSize: "0.875rem",
  borderRight: "1px solid red",
  borderColor: "primary.main",
  textAlign: "center",
  padding: 0.2,
  "&:last-child": { borderRight: "none" },
};

export default function IntegrationDetailsTable({
  history,
  isFetching,
  sortBy,
  sortOrder,
  onSortChange,
  timezone,
}: IntegrationDetailsTableProps) {
  if (history.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "calc(100vh - 350px)",
        }}
      >
        <Typography p={2} variant="h6" align="center" color="textSecondary">
          No history found
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer
      component={Paper}
      sx={{
        minHeight: 0,
        borderRadius: 2,
        boxShadow: 1,
        border: "1px solid",
        borderColor: "divider",
        maxHeight: 560,
        overflow: "auto",
      }}
    >
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={headerCellStyles}>
              <TableSortLabel
                active={sortBy === "date"}
                direction={sortBy === "date" ? sortOrder : "asc"}
                onClick={() => onSortChange("date")}
                sx={{
                  color: "white !important",
                  "&:hover": { color: "white" },
                  "& .MuiTableSortLabel-icon": { color: "white !important" },
                  marginLeft: "18px",
                }}
              >
                Date
              </TableSortLabel>
            </TableCell>
            <TableCell sx={headerCellStyles}>
              <TableSortLabel
                active={sortBy === "completed"}
                direction={sortBy === "completed" ? sortOrder : "asc"}
                onClick={() => onSortChange("completed")}
                sx={{
                  color: "white !important",
                  "&:hover": { color: "white" },
                  "& .MuiTableSortLabel-icon": { color: "white !important" },
                  marginLeft: "18px",
                }}
              >
                Completed
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {history.map((detail, index) => (
            <TableRow
              key={index}
              sx={{
                "&:last-child td, &:last-child th": {
                  borderBottom: "none",
                },
              }}
            >
              <TableCell sx={bodyCellStyles}>
                {timezone
                  ? `${formatWeekday(detail.date)}, ${formatDateTime(detail.date)}`
                  : new Date(detail.date * 1000).toLocaleString()}
              </TableCell>
              <TableCell sx={bodyCellStyles}>
                {detail.completed ? (
                  <CheckIcon sx={{ color: "secondary.main" }} />
                ) : (
                  <CloseIcon sx={{ color: "red" }} />
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
